import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { merge, Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { NativeAppService } from '@app/shared/native-app.service';

/**
 * This service's main method decides whether the navbar should be hidden or not. It allows components that don't want
 * to show the navbar to communicate with the components that can control the navbar.
 *
 * It checks conditions to determine if the navbar should be hidden:
 *   1. The path's leaf route's `data.hideNavbar` value (always takes precedence).
 *   2. The app being loaded inside a mobile WebView (can be overridden with query parameter).
 *
 * WebView Override: When in WebView, you can force the navbar to show by adding `?show_navbar=true` to the URL.
 *
 * @example Route configuration to hide navbar:
 * ```
 *   const routes: Routes = [
 *   ...
 *   {
 *     path: 'some-path',
 *     component: someComponent,
 *     data: {
 *       hideNavbar: true,
 *     },
 *   },
 *   ...
 * ];
 * ```
 *
 * @example URL to show navbar in WebView:
 * ```
 * https://example.com/some-path?is_web_view=true&show_navbar=true
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class NavbarService {
  constructor(private route: ActivatedRoute, private router: Router, private nativeAppService: NativeAppService) {}

  shouldHideNavbar$(): Observable<boolean> {
    const initialRouteData$ = this.getLeafRoute().data;
    const navigationEndRouteData$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      switchMap(() => this.getLeafRoute().data),
    );

    return merge(initialRouteData$, navigationEndRouteData$).pipe(
      map(routeData => {
        if (routeData.hideNavbar) {
          return true;
        }

        const isWebView = this.nativeAppService.isWebView(this.route);
        if (!isWebView) {
          return false;
        }

        const urlParams = new URLSearchParams(window.location.search);
        const showNavbarFromUrl = urlParams.get('show_navbar');

        return showNavbarFromUrl !== 'true';
      }),
    );
  }

  shouldHideFooter$(): Observable<boolean> {
    const initialRouteData$ = this.getLeafRoute().data;
    const navigationEndRouteData$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      switchMap(() => this.getLeafRoute().data),
    );

    return merge(initialRouteData$, navigationEndRouteData$).pipe(map(routeData => !!routeData.hideFooter));
  }

  private getLeafRoute(): ActivatedRoute {
    let currentRoute = this.route;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    return currentRoute;
  }
}
